/**
 * 数据契约（契约 §2）与请求/响应契约（契约 §3）的 zod schema + 推导类型。
 *
 * 三个刻意的设计决定：
 * 1) 请求体 schema 一律 `.strict()`：未知字段直接 40001。服务端字段（safetyFlag /
 *    promptTokens 等）绝不允许被客户端注入，静默 strip 会让攻击面不可见。
 * 2) 响应 DTO schema 保留默认的 strip 行为：上游加字段时老客户端仍能解析，
 *    这对三端灰度发布是必需的。
 * 3) 所有时间统一 `.datetime({ offset: false })`，即只接受 `...Z`。
 *    松散解析 `+08:00` 会让"日额度重置"这种按 UTC 自然日切分的逻辑出现跨日偏移。
 */

import { z } from 'zod';
import {
  AGE_BANDS,
  DOMAINS,
  EMAIL_PROVIDERS,
  LLM_PROVIDERS,
  ROUTE_SOURCES,
  SUSPENSION_KINDS,
  SUSPENSION_MAX_HOURS,
  USER_ROLES,
} from './domain.js';
import { BILLING_CYCLES, ORDER_STATUSES, PAID_PLAN_IDS, PAYMENT_CHANNELS, PLAN_IDS, QUOTA_SCOPES } from './plans.js';

/**
 * 结构化约束：任何"能被安全解析"的 schema。
 * 刻意不 import zod 的类型：那会把本文件与某一个 zod 大版本绑死
 * （z4 用 ZodType<Out, In>，z3 用 ZodType<Out, Def, In>），
 * 而这个仓库的 contracts 包必须同时对三端可编译。
 */
export interface ParseableSchema<T> {
  safeParse(input: unknown): { success: true; data: T } | { success: false; error: unknown };
}

/* ------------------------------------------------------------------ *
 * 基础标量
 * ------------------------------------------------------------------ */

/** E.164：`+` 开头，国家码非 0 起头，总长 8-15 位数字（契约 §2 phone 示例 +8613800138000） */
export const PhoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{7,14}$/, '手机号格式不正确，应形如 +8613800138000');

/**
 * 邮箱地址（契约 §3.1 邮箱验证码登录）。
 *
 * 为什么在 schema 层统一 `toLowerCase()`：邮箱的本地部分在 RFC 里理论上大小写敏感，
 * 但没有任何一家主流服务商真的区分；若不在入口归一，"注册时填 Bob@x.com、登录时填
 * bob@x.com"要么被当成两个账号，要么直接撞 `users.email` 的唯一索引报 50000。
 * 归一放在契约里（而不是各个调用点）才能保证**落库值、验证码缓存键、频控键**永远是同一形态。
 *
 * 254 是 RFC 5321 的地址总长上限（含 @ 与域名），比数据库列宽短一点没有意义，
 * 这里与 `prisma/schema.prisma` 的 `@db.VarChar(254)` 严格一致。
 */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('邮箱格式不正确')
  .max(254, '邮箱长度不能超过 254 个字符');

/** 数据主键：契约 §0 要求一律 cuid2 字符串（cuid2 默认 24 字符，这里放宽到 8-64 以兼容旧 cuid） */
export const IdSchema = z.string().trim().min(8).max(64);

export const RequestIdSchema = z.string().trim().min(1).max(128);

/**
 * 用户 id：v1.4.1 起 `users.id` 是整数自增（按注册顺序从 1 起，管理员与 ROOT 也在 users 表里），
 * 但契约里仍以**字符串**下发（`"1"`、`"2"`）。
 *
 * 为什么不把 JSON 类型也改成 number：客户端大量以 id 做 Map 键/相等比较，
 * 改类型会连带动 Web 与移动端所有 id 处理，而这次需求只要求"生成逻辑用纯数字"。
 *
 * 为什么不复用下面的 `IdSchema`：它要求 8-64 字符，正是为了挡住 cuid 之外的形态，
 * 纯数字 id（如 "1"）在它这里过不去。两者刻意不合并——会话/消息/账本/渠道/订单的 id
 * 仍是 cuid，将来任一侧改形态时约束必须能分别调整。
 */
export const UserIdSchema = z
  .string()
  .trim()
  .regex(/^[1-9]\d{0,9}$/, '用户 id 必须是十进制数字（按注册顺序自 1 起）')
  .refine((value) => Number(value) <= 2_147_483_647, {
    message: '用户 id 超出 32 位整数范围（users.id 是 MySQL INT）',
  });

/** ISO-8601 UTC，必须带 Z 后缀 */
export const IsoDateTimeSchema = z
  .string()
  .refine((value) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/.test(value) && !Number.isNaN(Date.parse(value)), {
    message: '时间必须为 ISO-8601 UTC 字符串，如 2026-02-14T03:21:07.512Z',
  });

/** 请求头 Idempotency-Key（契约 §0：写操作支持，服务端保留 24h） */
export const IdempotencyKeySchema = z.string().trim().min(8).max(128);

export const IDEMPOTENCY_KEY_TTL_SECONDS = 86_400;

/* ------------------------------------------------------------------ *
 * 枚举 schema（领域/年龄段的真源在 domain.ts，此处只做 zod 包装）
 * ------------------------------------------------------------------ */

export const DomainSchema = z.enum(DOMAINS);
export const AgeBandSchema = z.enum(AGE_BANDS);
export const RouteSourceSchema = z.enum(ROUTE_SOURCES);
export const PlanIdSchema = z.enum(PLAN_IDS);

/** 可下单的档位（契约 §3.5）：`free` 不是商品、`unlimited` 是内部档位，都不接受购买 */
export const PaidPlanIdSchema = z.enum(PAID_PLAN_IDS);

export const BillingCycleSchema = z.enum(BILLING_CYCLES);
export type BillingCycleValue = z.infer<typeof BillingCycleSchema>;

export const PaymentChannelSchema = z.enum(PAYMENT_CHANNELS);
export type PaymentChannelValue = z.infer<typeof PaymentChannelSchema>;

export const OrderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatusValue = z.infer<typeof OrderStatusSchema>;

export const OrderKindSchema = z.enum(['purchase', 'renewal', 'upgrade']);
export type OrderKindValue = z.infer<typeof OrderKindSchema>;

export const UserStatusSchema = z.enum(['active', 'suspended', 'deleted']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

/** 三级身份（user/admin/root）。真源在 domain.ts，这里只做 zod 包装。 */
export const UserRoleSchema = z.enum(USER_ROLES);
export type UserRoleValue = z.infer<typeof UserRoleSchema>;

export const SuspensionKindSchema = z.enum(SUSPENSION_KINDS);
export type SuspensionKindValue = z.infer<typeof SuspensionKindSchema>;

export const LlmProviderSchema = z.enum(LLM_PROVIDERS);
export type LlmProviderValue = z.infer<typeof LlmProviderSchema>;

/** 邮箱发信协议（v1.5.0）。真源在 domain.ts，这里只做 zod 包装。 */
export const EmailProviderSchema = z.enum(EMAIL_PROVIDERS);
export type EmailProviderValue = z.infer<typeof EmailProviderSchema>;

export const MessageRoleSchema = z.enum(['user', 'assistant', 'system']);
export type MessageRole = z.infer<typeof MessageRoleSchema>;

export const SafetyFlagSchema = z.enum(['none', 'blocked', 'redirected', 'flagged']);
export type SafetyFlag = z.infer<typeof SafetyFlagSchema>;

export const PreferredToneSchema = z.enum(['gentle', 'firm', 'balanced']);
export type PreferredTone = z.infer<typeof PreferredToneSchema>;

/** 契约 §4 SafetyVerdict.action */
export const SafetyActionSchema = z.enum(['allow', 'block', 'redirect', 'flag']);
export type SafetyAction = z.infer<typeof SafetyActionSchema>;

export const RiskLevelSchema = z.enum(['none', 'low', 'medium', 'high', 'critical']);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

/** 契约 §4 SafetyEvent.stage */
export const SafetyStageSchema = z.enum(['input', 'output']);
export type SafetyStage = z.infer<typeof SafetyStageSchema>;

/**
 * QuotaLedger.kind。
 *
 * `reserve`/`commit`/`release` 是 Lead Directives §5.5 的两阶段额度；
 * `grant` 是 ROOT 手动发放（用户裁决 2026-10-10）——它**不写 Redis 计数器**，
 * 而是在月度口径里做减项：可用量 = 额度上限 − (已 commit − 已 grant)。
 */
export const LedgerKindSchema = z.enum(['reserve', 'commit', 'release', 'grant']);
export type LedgerKind = z.infer<typeof LedgerKindSchema>;

/* ------------------------------------------------------------------ *
 * §0.1 统一信封
 * ------------------------------------------------------------------ */

export interface ApiSuccessEnvelope<T> {
  code: 0;
  message: 'ok' | string;
  data: T;
  requestId: string;
}

/**
 * 为任意 data schema 生成成功信封 schema（把信封形状也纳入校验，避免后端漏写 requestId）。
 *
 * 为什么必须显式标注返回类型：若交给 TS 推断，推断产物会经过 zod 的
 * `addQuestionMarks` 工具类型，它给每个字段都加上 `?`，于是 `data` 的**输出**类型
 * 变成 `T | undefined`。那会让 `unwrapEnvelope` 无法返回 `T | null`，
 * 且下游 `res.data.xxx` 全部报"可能为 undefined"，逼迫调用方写无意义的空值判断。
 * 显式声明成 `z.ZodType<T>` 后，输出类型就是 `T`，同时也免掉了一处
 * `as unknown as` 断言（规范禁止 `as any`，能去掉的断言都该去掉）。
 */
export function envelopeSchema<T>(dataSchema: ParseableSchema<T>): z.ZodObject<{
  code: z.ZodLiteral<0>;
  message: z.ZodString;
  data: z.ZodType<T>;
  requestId: typeof RequestIdSchema;
}> {
  return z.object({
    code: z.literal(0),
    message: z.string(),
    data: dataSchema as unknown as z.ZodType<T>,
    requestId: RequestIdSchema,
  });
}

/** 解析信封并解包 data；失败时返回 null，由调用方决定抛什么 AppException */
export function unwrapEnvelope<T>(raw: unknown, dataSchema: ParseableSchema<T>): T | null {
  // 这个局部标注不是冗余的：`envelopeSchema` 的返回类型由**泛型实参**推出，
  // 而实参来自 `ParseableSchema<T>` 这个接口（不是 `z.ZodType<T>`），
  // 于是 safeParse 的产物会经过 zod 的 addQuestionMarks 从而把 data 变成可选。
  // 在此处显式约束一次，`parsed.data.data` 才是裸 `T`，也才能返回 `T | null`
  // 而不是 `T | null | undefined`（后者会逼所有调用方写无意义的空值判断）。
  const schema: z.ZodType<ApiSuccessEnvelope<T>> = envelopeSchema(dataSchema) as unknown as z.ZodType<
    ApiSuccessEnvelope<T>
  >;
  const parsed = schema.safeParse(raw);
  return parsed.success ? parsed.data.data : null;
}

/* ------------------------------------------------------------------ *
 * §2 数据契约
 * ------------------------------------------------------------------ */

export const UserSchema = z.object({
  /** v1.4.1 起是十进制数字字符串（`"1"` 起，按注册顺序），不再是 cuid，故用 UserIdSchema */
  id: UserIdSchema,
  /**
   * v1.4.0 起**必填**。之前不存在该字段，老客户端拿到的对象里没有它也不会崩
   * （响应 DTO 是 strip 语义，不是 strict），但服务端所有构造 User 的地方都必须补上。
   */
  role: UserRoleSchema,
  phone: PhoneSchema.nullable(),
  email: EmailSchema.nullable(),
  wechatOpenId: z.string().min(1).max(128).nullable(),
  nickname: z.string().min(1).max(32),
  avatarUrl: z.string().url().max(2048).nullable(),
  status: UserStatusSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type User = z.infer<typeof UserSchema>;

export const ProfileSchema = z.object({
  id: IdSchema,
  userId: UserIdSchema,
  childAgeBands: z.array(AgeBandSchema).max(4),
  childCount: z.number().int().min(0).max(10),
  concerns: z.array(DomainSchema).max(DOMAINS.length),
  preferredTone: PreferredToneSchema,
  extra: z.record(z.unknown()),
});
export type Profile = z.infer<typeof ProfileSchema>;

export const ConversationSchema = z.object({
  id: IdSchema,
  userId: UserIdSchema,
  title: z.string().min(1).max(100),
  domain: DomainSchema,
  ageBand: AgeBandSchema.nullable(),
  messageCount: z.number().int().min(0),
  lastMessageAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const MessageSchema = z.object({
  id: IdSchema,
  conversationId: IdSchema,
  role: MessageRoleSchema,
  content: z.string().max(200_000),
  domain: DomainSchema.nullable(),
  ageBand: AgeBandSchema.nullable(),
  safetyFlag: SafetyFlagSchema,
  latencyMs: z.number().int().min(0),
  upstreamModel: z.string().max(128).nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Message = z.infer<typeof MessageSchema>;

/**
 * 额度快照（契约 §2 `Quota`，**v1.3.0 起只给百分比**）。
 *
 * v1.2.0 及以前这里下发的是 `dailyTokenLimit / dailyTokenUsed / monthlyTokenLimit /
 * monthlyTokenUsed / remainingToday` 五个绝对 token 量，由前端自己算百分比。
 * 用户裁决 2026-10-05：**前端不显示、也不持有任何真实 token 量**，所有消耗计算
 * 与百分比换算留在后端。于是这五个字段全部删除，换成"哪一维 + 剩余百分比"。
 *
 * `scope` 与 `resetAt` 必须成对使用：`scope` 说这个百分比属于哪一维，
 * `resetAt` 就是那一维的重置时刻。
 */
export const QuotaSchema = z.object({
  planId: PlanIdSchema,
  scope: z.enum(QUOTA_SCOPES),
  remainingPercent: z.number().int().min(0).max(100),
  unlimited: z.boolean(),
  resetAt: IsoDateTimeSchema,
});
export type Quota = z.infer<typeof QuotaSchema>;

/**
 * 42900 `QUOTA_EXCEEDED` 的 `details`（契约 §4.4）。
 *
 * 同样只给百分比：前端弹窗要说的是"哪个维度用完了、还剩多少、什么时候重置"，
 * 这些都是展示信息，没有任何理由把绝对 token 量交给客户端。
 */
export const QuotaExceededDetailsSchema = z.object({
  scope: z.enum(QUOTA_SCOPES),
  remainingPercent: z.number().int().min(0).max(100),
  resetAt: IsoDateTimeSchema,
});
export type QuotaExceededDetails = z.infer<typeof QuotaExceededDetailsSchema>;

/* ------------------------------------------------------------------ *
 * §4 安全契约
 * ------------------------------------------------------------------ */

export const SafetyVerdictSchema = z.object({
  action: SafetyActionSchema,
  riskLevel: RiskLevelSchema,
  categories: z.array(z.string().min(1).max(64)).max(16),
  reason: z.string().max(2000),
  publicMessage: z.string().min(1).max(1000),
  redirectTo: z.string().max(200).nullable(),
});
export type SafetyVerdict = z.infer<typeof SafetyVerdictSchema>;

export const SafetyEventSchema = z.object({
  id: IdSchema,
  userId: UserIdSchema,
  conversationId: IdSchema.nullable(),
  messageId: IdSchema.nullable(),
  stage: SafetyStageSchema,
  verdict: SafetyVerdictSchema,
  createdAt: IsoDateTimeSchema,
});
export type SafetyEventDto = z.infer<typeof SafetyEventSchema>;

/** 全国心理援助热线（契约 §4 强制规则 3：critical 转介话术必须包含） */
export const CRISIS_HOTLINE = '12356';

/* ------------------------------------------------------------------ *
 * §3.1 认证
 * ------------------------------------------------------------------ */

export const SmsSceneSchema = z.enum(['login', 'bind', 'reset']);
export type SmsScene = z.infer<typeof SmsSceneSchema>;

export const DeviceIdSchema = z.string().trim().min(4).max(64);

export const SendSmsSchema = z
  .object({
    phone: PhoneSchema,
    scene: SmsSceneSchema,
  })
  .strict();
export type SendSmsInput = z.infer<typeof SendSmsSchema>;

export const SendSmsResponseSchema = z.object({
  expireSeconds: z.number().int().positive(),
  cooldownSeconds: z.number().int().nonnegative(),
});
export type SendSmsResponse = z.infer<typeof SendSmsResponseSchema>;

/**
 * 验证码固定 6 位数字。短信与邮箱**共用同一个形态**（同一个 6 位数字码机制），
 * 所以只定义一次再导出两个名字：将来若要改成 4 位或字母数字，
 * 只能改这一处，不会出现"短信 6 位、邮箱 4 位"这种只在某个调用点生效的分叉。
 */
const SixDigitCodeSchema = z.string().regex(/^\d{6}$/, '验证码为 6 位数字');

export const SmsCodeSchema = SixDigitCodeSchema;

export const VerifySmsSchema = z
  .object({
    phone: PhoneSchema,
    code: SmsCodeSchema,
    deviceId: DeviceIdSchema,
  })
  .strict();
export type VerifySmsInput = z.infer<typeof VerifySmsSchema>;

/* ------------------------- 邮箱验证码登录（v1.5.0） ------------------------- */

export const EmailCodeSchema = SixDigitCodeSchema;

/**
 * 发送邮箱验证码。与短信同形（`scene` 复用 `SmsSceneSchema` 的三值），
 * 因为"登录/绑定/找回"这三种用途的**频控与文案策略是同一套**；
 * 若为邮箱另造一套 scene，两个枚举迟早会漂移。
 */
export const SendEmailSchema = z
  .object({
    email: EmailSchema,
    scene: SmsSceneSchema,
  })
  .strict();
export type SendEmailInput = z.infer<typeof SendEmailSchema>;

export const SendEmailCodeResponseSchema = z.object({
  expireSeconds: z.number().int().positive(),
  cooldownSeconds: z.number().int().nonnegative(),
});
export type SendEmailCodeResponse = z.infer<typeof SendEmailCodeResponseSchema>;

export const VerifyEmailSchema = z
  .object({
    email: EmailSchema,
    code: EmailCodeSchema,
    deviceId: DeviceIdSchema,
  })
  .strict();
export type VerifyEmailInput = z.infer<typeof VerifyEmailSchema>;

export const WechatLoginSchema = z
  .object({
    code: z.string().trim().min(1).max(256),
    deviceId: DeviceIdSchema,
  })
  .strict();
export type WechatLoginInput = z.infer<typeof WechatLoginSchema>;

export const RefreshTokenSchema = z
  .object({
    refreshToken: z.string().trim().min(8).max(512),
  })
  .strict();
export type RefreshTokenInput = z.infer<typeof RefreshTokenSchema>;

/**
 * 登录/刷新通用响应。
 * needBindPhone 只在微信首登时为 true，其余场景省略（而非 false），
 * 前端据此判断"是否要弹绑定手机号弹窗"，省略能避免误弹。
 */
export const AuthTokensSchema = z.object({
  accessToken: z.string().min(8),
  refreshToken: z.string().min(8),
  expiresIn: z.number().int().positive(),
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

export const AuthResultSchema = AuthTokensSchema.extend({
  user: UserSchema,
  profile: ProfileSchema.nullable(),
  needBindPhone: z.boolean().optional(),
});
export type AuthResult = z.infer<typeof AuthResultSchema>;

export const RefreshResultSchema = AuthTokensSchema;
export type RefreshResult = z.infer<typeof RefreshResultSchema>;

/** GET /auth/me —— 前端冷启动唯一聚合接口 */
export const MeResponseSchema = z.object({
  user: UserSchema,
  profile: ProfileSchema.nullable(),
  /**
   * v1.4.0 起可为 null：管理员与 ROOT 没有个人额度（他们不发起对话）。
   * 用 null 而不是造一个 free 的假额度，是因为后台外壳根本不渲染额度条，
   * 给一个假的会让"这是谁的额度"变得含混。
   */
  quota: QuotaSchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** accessToken TTL 7200s / refreshToken TTL 30d（契约 §3.1，与环境变量 JWT_*_TTL 对应） */
export const ACCESS_TOKEN_TTL_SECONDS = 7200;
export const REFRESH_TOKEN_TTL_SECONDS = 2_592_000;

export const SmsExpireSeconds = 300;
export const SmsCooldownSeconds = 60;

/**
 * 邮箱验证码有效期与重发冷却。
 *
 * 有效期与短信同为 300s：邮箱到达通常比短信快且不额外计费，没有理由放宽
 * （放宽只会扩大"验证码被翻到"的窗口）；冷却同为 60s，因为两者共用同一条
 * "按目标地址整分钟频控"的规则（键 `rl:code:{scene}:{target}:{minute}`）。
 */
export const EmailExpireSeconds = 300;
export const EmailCooldownSeconds = 60;

/* ------------------------------------------------------------------ *
 * §3.2 画像
 * ------------------------------------------------------------------ */

export const UpdateProfileSchema = z
  .object({
    childAgeBands: z.array(AgeBandSchema).max(4).optional(),
    childCount: z.number().int().min(0).max(10).optional(),
    concerns: z.array(DomainSchema).max(DOMAINS.length).optional(),
    preferredTone: PreferredToneSchema.optional(),
    extra: z.record(z.unknown()).optional(),
  })
  .strict();
export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>;

/** PATCH：局部合并，extra 深合并一层（契约 §3.2）；键集合与 PUT 相同，语义由服务端区分 */
export const PatchProfileSchema = UpdateProfileSchema;
export type PatchProfileInput = z.infer<typeof PatchProfileSchema>;

export const ProfileResponseSchema = z.object({
  profile: ProfileSchema,
});
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

/* ------------------------------------------------------------------ *
 * §3.3 对话
 * ------------------------------------------------------------------ */

export const CHAT_MESSAGE_MIN_LENGTH = 1;
export const CHAT_MESSAGE_MAX_LENGTH = 4000;
/** 历史消息滑窗（Lead Directives §5.4）：最近 10 轮 = 20 条 */
export const CHAT_HISTORY_MAX_TURNS = 10;
export const CHAT_HISTORY_MAX_MESSAGES = CHAT_HISTORY_MAX_TURNS * 2;
export const CHAT_HISTORY_MAX_TOKENS = 6000;
/** 会话标题自动截断长度（契约 §2 Conversation.title） */
export const CONVERSATION_TITLE_MAX_LENGTH = 20;

export const ChatCompletionsRequestSchema = z
  .object({
    conversationId: IdSchema.optional(),
    message: z
      .string()
      .trim()
      .min(CHAT_MESSAGE_MIN_LENGTH, '消息不能为空')
      .max(CHAT_MESSAGE_MAX_LENGTH, `消息长度不能超过 ${CHAT_MESSAGE_MAX_LENGTH} 字`),
    domain: DomainSchema.optional(),
    ageBand: AgeBandSchema.optional(),
    stream: z.boolean().default(true),
    /** v1 保留字段：必须为空数组，非空一律 40001（契约 §3.3 逐字要求） */
    attachments: z
      .array(z.never())
      .max(0, 'attachments 为 v1 保留字段，必须为空数组')
      .default([]),
  })
  .strict()
  .superRefine((value, ctx) => {
    // 只有 family_education 消费年龄段；preferredTone 等其它领域传了会污染落库字段
    if (value.ageBand !== undefined && value.domain !== undefined && value.domain !== 'family_education') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ageBand'],
        message: 'ageBand 仅在 domain=family_education 时有意义',
      });
    }
  });
export type ChatCompletionsInput = z.infer<typeof ChatCompletionsRequestSchema>;

/**
 * 一次生成如何结束。
 *
 * 这是本服务的**受控词汇表**，不得出现上游厂商词汇：上游 OpenAI 兼容流返回
 * `content_filter` 时，由服务端在写 done 帧前映射为 `safety_blocked` 并记日志。
 * 若任由上游枚举穿透到对外契约，前端的判断逻辑就被绑死在某个供应商的命名上，
 * 而这个耦合将来无法移除。
 *
 * 唯一定义点：`sse.ts` 的 `SseDonePayloadSchema` 直接 import 本 schema 复用，
 * 因此不存在"两处枚举漂移"的可能（若各写一份 `z.enum`，漂移是迟早的事）。
 */
export const ChatFinishReasonSchema = z.enum(['stop', 'length', 'aborted', 'safety_blocked']);
export type ChatFinishReason = z.infer<typeof ChatFinishReasonSchema>;

/**
 * stream=false 的同步返回 data（契约 §3.3）。
 *
 * v1.3.0 起**不再带 `usage`**（`{promptTokens, completionTokens, totalTokens}`）：
 * 单轮消耗的绝对 token 量属于服务端内部对账信息，不下发客户端。
 * 客户端需要知道的"额度还剩多少"由 `quota` 的百分比回答。
 */
export const ChatCompletionsResponseSchema = z.object({
  conversationId: IdSchema,
  message: MessageSchema,
  quota: QuotaSchema,
});
export type ChatCompletionsResponse = z.infer<typeof ChatCompletionsResponseSchema>;

export const RouteRequestSchema = z
  .object({
    message: z.string().trim().min(1).max(CHAT_MESSAGE_MAX_LENGTH),
    /** 只带路由真正需要的字段：前端可预探测而不必先登录拉完整 Profile */
    profile: z
      .object({
        childAgeBands: z.array(AgeBandSchema).max(4).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type RouteRequestInput = z.infer<typeof RouteRequestSchema>;

export const RouteResponseSchema = z.object({
  domain: DomainSchema,
  ageBand: AgeBandSchema.nullable(),
  confidence: z.number().min(0).max(1),
  reason: z.string().max(500),
  source: RouteSourceSchema,
});
export type RouteResponse = z.infer<typeof RouteResponseSchema>;

export const UpdateConversationSchema = z
  .object({
    title: z.string().trim().min(1).max(100),
  })
  .strict();
export type UpdateConversationInput = z.infer<typeof UpdateConversationSchema>;

export const ListConversationsQuerySchema = z
  .object({
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    domain: DomainSchema.optional(),
  })
  .strict();
export type ListConversationsQuery = z.infer<typeof ListConversationsQuerySchema>;

export const ListMessagesQuerySchema = z
  .object({
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type ListMessagesQuery = z.infer<typeof ListMessagesQuerySchema>;

/* ------------------------------------------------------------------ *
 * §3.4 额度
 * ------------------------------------------------------------------ */

export const QuotaResponseSchema = z.object({
  quota: QuotaSchema,
});
export type QuotaResponse = z.infer<typeof QuotaResponseSchema>;

/**
 * 账本条目（消耗明细的"按次"视图）。
 *
 * v1.3.0 起删掉 `totalTokens`：前端此前是拿 `totalTokens < 0` 猜"这一行是返还"，
 * 等于把"数额的符号"当枚举用。下发账本本来就有的 `kind` 既精确，又不必把余额
 * 数额交给客户端。
 */
export const LedgerEntrySchema = z.object({
  id: IdSchema,
  conversationId: IdSchema.nullable(),
  kind: LedgerKindSchema,
  createdAt: IsoDateTimeSchema,
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

export const ListLedgerQuerySchema = z
  .object({
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type ListLedgerQuery = z.infer<typeof ListLedgerQuerySchema>;

/**
 * 按会话聚合的用量查询（契约 §3.4 `GET /quota/ledger/conversations`）。
 *
 * 默认窗口是**当前 UTC 自然月**，与月额度重置口径一致：用户问"额度被谁吃掉了"，
 * 问的就是这一期，页面上的合计必须与额度条说的是同一段时间。
 * `from`/`to` 是给对账用的逃生口（半开区间 `[from, to)`），**必须同时给或同时不给**：
 * 只给一半就得由服务端猜另一半，猜出来的窗口一旦不是自然月，合计就再也没法和额度对上。
 */
export const ListLedgerConversationsQuerySchema = z
  .object({
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict()
  .refine((value) => (value.from === undefined) === (value.to === undefined), {
    message: 'from 与 to 必须同时提供',
    path: ['to'],
  });
export type ListLedgerConversationsQuery = z.infer<typeof ListLedgerConversationsQuerySchema>;

/**
 * 单个会话在窗口内的用量。
 *
 * `conversationId === null` = 这些轮次没有关联会话（早期数据），不是错误，前端要单独归一行。
 *
 * v1.3.0 起 `totalTokens` 换成 `sharePercent`（该会话占本窗口全部消耗的百分比）：
 * 用户要看的是"谁吃掉了最多额度"，而这件事用相对占比表达得更清楚，
 * 也不必把绝对 token 量交给客户端。取整可能让各项之和不等于 100，**这是展示值**。
 */
export const ConversationUsageSchema = z.object({
  conversationId: IdSchema.nullable(),
  rounds: z.number().int().min(0),
  sharePercent: z.number().int().min(0).max(100),
  lastAt: IsoDateTimeSchema,
});
export type ConversationUsage = z.infer<typeof ConversationUsageSchema>;

/**
 * 按会话聚合的用量响应。
 *
 * `rounds`/`itemCount` 是**整个窗口**的合计，不是 `items` 的和：`truncated` 为 true 时
 * 还有分组没返回，合计仍必须精确，否则"本月用了多少轮"会随 limit 变化——那是同一个页面
 * 上两处互相矛盾的数字。
 */
export const ConversationUsageResponseSchema = z.object({
  windowStart: IsoDateTimeSchema,
  windowEnd: IsoDateTimeSchema,
  rounds: z.number().int().min(0),
  itemCount: z.number().int().min(0),
  items: z.array(ConversationUsageSchema),
  truncated: z.boolean(),
});
export type ConversationUsageResponse = z.infer<typeof ConversationUsageResponseSchema>;

/* ------------------------------------------------------------------ *
 * 分页
 * ------------------------------------------------------------------ */

export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 100;

/**
 * 游标分页响应。nextCursor 为 null 表示没有下一页。
 * 用游标而非 offset：会话列表按 lastMessageAt 倒序且会被新消息改动，
 * offset 分页会产生重复/漏项。
 */
export function cursorPageSchema<T>(itemSchema: ParseableSchema<T>) {
  return z.object({
    items: z.array(itemSchema as unknown as z.ZodType<T>),
    nextCursor: z.string().max(256).nullable(),
  });
}

export const ConversationPageSchema = cursorPageSchema(ConversationSchema);
export type ConversationPage = z.infer<typeof ConversationPageSchema>;

export const MessagePageSchema = cursorPageSchema(MessageSchema);
export type MessagePage = z.infer<typeof MessagePageSchema>;

export const LedgerPageSchema = cursorPageSchema(LedgerEntrySchema);
export type LedgerPage = z.infer<typeof LedgerPageSchema>;

/* ------------------------------------------------------------------ *
 * §3.5 套餐订阅与支付
 * ------------------------------------------------------------------ */

/** 订单号：`SP` + 时间戳 + 随机段，cuid2 之外唯一一处自定义 id 形态（要能直接报给客服看） */
export const OrderNoSchema = z
  .string()
  .trim()
  .regex(/^SP[0-9A-Z]{10,30}$/, '订单号格式不正确');

/**
 * 当前生效订阅。未购买过的用户返回 `null`（而不是一个 free 的假订阅）：
 * "没有订阅"与"订阅了 free"在续费顺延的算法里是不同的输入，
 * 用 null 表达前者可以让 `quotePlanChange()` 的分支保持诚实。
 */
export const SubscriptionSchema = z.object({
  planId: PlanIdSchema,
  billingCycle: BillingCycleSchema,
  startsAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  /** 产生这份订阅的订单号，用于客服定位 */
  orderNo: OrderNoSchema,
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

export const SubscriptionResponseSchema = z.object({
  subscription: SubscriptionSchema.nullable(),
});
export type SubscriptionResponse = z.infer<typeof SubscriptionResponseSchema>;

export const OrderSchema = z.object({
  orderNo: OrderNoSchema,
  planId: PlanIdSchema,
  billingCycle: BillingCycleSchema,
  kind: OrderKindSchema,
  /** 单位：分。服务端依据 `PLAN_PRICES` 算出，客户端传来的任何金额都不参与 */
  amountFen: z.number().int().min(0),
  status: OrderStatusSchema,
  channel: PaymentChannelSchema,
  /** 微信 Native 支付链接（`weixin://wxpay/bizpayurl?...`），非微信单为 null */
  codeUrl: z.string().max(512).nullable(),
  /** 后端生成的二维码图片 data URL（用户决策：前端不引二维码依赖），非微信单为 null */
  qrImageDataUrl: z.string().max(8192).nullable(),
  /** 支付宝电脑网站支付跳转链接，非支付宝单为 null */
  payUrl: z.string().max(1024).nullable(),
  /** 订单**下单时算定**的权益区间快照：用户看到的价格与拿到的时长必须一致 */
  grantStartsAt: IsoDateTimeSchema,
  grantExpiresAt: IsoDateTimeSchema,
  /** 订单支付截止时间（用户决策：15 分钟），超时后 `status` 变 `closed` */
  payExpiresAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
  paidAt: IsoDateTimeSchema.nullable(),
});
export type Order = z.infer<typeof OrderSchema>;

export const OrderResponseSchema = z.object({
  order: OrderSchema,
});
export type OrderResponse = z.infer<typeof OrderResponseSchema>;

/** 下单请求体。`.strict()` + 只允许 `PAID_PLAN_IDS`：`free`/`unlimited` 在入口即 40001 */
export const CreateOrderRequestSchema = z
  .object({
    planId: PaidPlanIdSchema,
    billingCycle: BillingCycleSchema,
    channel: PaymentChannelSchema,
  })
  .strict();
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

/**
 * 主动同步（轮询）与 mock 支付的统一响应：把"订单新状态 + 最新额度 + 最新订阅"一次带回。
 *
 * 三件一起返回是有意的：前端支付成功后要同时刷新额度条、套餐卡片与订单状态，
 * 分成三个请求会出现"额度已变但套餐显示还是旧的"这种中间态。
 */
export const PaymentResultResponseSchema = z.object({
  order: OrderSchema,
  quota: QuotaSchema,
  subscription: SubscriptionSchema.nullable(),
});
export type PaymentResultResponse = z.infer<typeof PaymentResultResponseSchema>;

/**
 * mock 通道手动触发支付结果（`PAYMENT_MOCK_ENABLED=true` **且** `NODE_ENV !== 'production'`
 * 时才可用，否则 40400）。生产环境服务端无条件拒绝该端点，与开关取值无关。
 * 存在的意义：没有真实商户凭据的环境里，支付闭环的**唯一**可验证路径。
 */
export const MockPayRequestSchema = z
  .object({
    outcome: z.enum(['success', 'failure']).default('success'),
  })
  .strict();
export type MockPayRequest = z.infer<typeof MockPayRequestSchema>;

/** 微信 v3 回调应答（契约 §3.5 明确记为**非信封**响应：渠道只认这个形状） */
export const WECHAT_NOTIFY_SUCCESS_BODY = Object.freeze({ code: 'SUCCESS', message: '成功' });

/** 支付宝回调应答：异步通知要求响应体为纯文本 `success` */
export const ALIPAY_NOTIFY_SUCCESS_BODY = 'success';

/* ------------------------------------------------------------------ *
 * §3.6 后台（管理员 / ROOT，用户裁决 2026-10-10）
 *
 * 这整段是**故意**唯一下发绝对 token 真值的地方。契约 v1.3.0 之后普通用户端点
 * 只给百分比，理由是"前端不持有真实消耗"。但 ROOT 的职责恰恰是"管理额度资源"——
 * 不给他真值就没法判断该发多少。例外只开在 /admin/** 上，且有 @Roles 守卫兜底。
 * ------------------------------------------------------------------ */

/** 管理员 / ROOT 的登录名。统一小写：大小写不敏感会让"Admin"和"admin"变成两个账号。 */
export const UsernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9_.-]{2,31}$/, '账号为 3-32 位小写字母、数字、下划线、点或连字符');

export const PasswordSchema = z.string().min(8, '密码至少 8 位').max(128);

export const PasswordLoginSchema = z
  .object({
    username: UsernameSchema,
    password: PasswordSchema,
    deviceId: DeviceIdSchema,
  })
  .strict();
export type PasswordLoginInput = z.infer<typeof PasswordLoginSchema>;

export const CreateAdminSchema = z
  .object({
    username: UsernameSchema,
    password: PasswordSchema,
    nickname: z.string().trim().min(1).max(32),
  })
  .strict();
export type CreateAdminInput = z.infer<typeof CreateAdminSchema>;

export const ResetPasswordSchema = z.object({ password: PasswordSchema }).strict();
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

export const SuspendUserSchema = z
  .object({
    kind: SuspensionKindSchema,
    durationHours: z.number().int().min(1).max(SUSPENSION_MAX_HOURS).optional(),
    reason: z.string().trim().min(1).max(200),
  })
  .strict()
  .refine((value) => value.kind !== 'temporary' || value.durationHours !== undefined, {
    message: '短期封号必须给出 durationHours',
    path: ['durationHours'],
  });
export type SuspendUserInput = z.infer<typeof SuspendUserSchema>;

/** 1 亿 token 上限纯粹是防手滑多打一个 0——真实发放量级远小于此。 */
export const GrantQuotaSchema = z
  .object({
    totalTokens: z.number().int().min(1).max(100_000_000),
    reason: z.string().trim().min(1).max(200),
  })
  .strict();
export type GrantQuotaInput = z.infer<typeof GrantQuotaSchema>;

export const SetUserPlanSchema = z
  .object({
    planId: PlanIdSchema,
    billingCycle: BillingCycleSchema,
    durationDays: z.number().int().min(1).max(3650),
  })
  .strict();
export type SetUserPlanInput = z.infer<typeof SetUserPlanSchema>;

/**
 * 渠道字段集中定义一次：新建要求全给，编辑要求全可选。
 *
 * 不用 `UpsertLlmChannelSchema.partial()` 是因为新建 schema 上带了 `.default()`，
 * 而 zod 的 `partial()` 会把"未传"重新落回默认值——PATCH 里 `enabled` 不传会被
 * 悄悄改成 `true`，把停用中的渠道又打开。两条 schema 因此必须分开写。
 *
 * v1.6.0：`chatModel` / `routerModel` / `models` 三个字段**从渠道上移除** ——
 * 模型选择搬到了 `UpsertLlmModelRouteSchema`（同一渠道可挂多个模型、跨渠道统一排序、
 * 失败可降级）。留着它们会变成第二真源：后台两处都能填模型名而只有一处生效，
 * 运营改了另一处却看不到任何变化。
 */
const llmChannelFields = {
  name: z.string().trim().min(1).max(64),
  provider: LlmProviderSchema,
  baseUrl: z.string().trim().url().max(512),
  apiKey: z.string().trim().min(8).max(512),
  enabled: z.boolean(),
  priority: z.number().int().min(0).max(1000),
  timeoutMs: z.number().int().min(1000).max(600_000),
  remark: z.string().trim().max(200).nullable(),
} as const;

export const UpsertLlmChannelSchema = z
  .object({
    ...llmChannelFields,
    enabled: llmChannelFields.enabled.default(true),
    priority: llmChannelFields.priority.default(100),
    timeoutMs: llmChannelFields.timeoutMs.default(60_000),
    remark: llmChannelFields.remark.default(null),
  })
  .strict();
export type UpsertLlmChannelInput = z.infer<typeof UpsertLlmChannelSchema>;

/** 编辑时省略 `apiKey` 表示"不改密钥"；给了才覆盖。 */
export const PatchLlmChannelSchema = z
  .object(llmChannelFields)
  .partial()
  .extend({ id: IdSchema })
  .strict();
export type PatchLlmChannelInput = z.infer<typeof PatchLlmChannelSchema>;

/* ---------------------- 模型路由（v1.6.0，仅 ROOT） ---------------------- */

/**
 * 模型用途：`chat` = 对话回答，`router` = 意图路由，`both` = 两者皆可。
 *
 * 为什么不做成两个布尔字段（isChat / isRouter）：`both` 是最常见的配置，
 * 而布尔组合里"两个都为 false"是语法上合法、语义上无解的输入 ——
 * 枚举把这种组合从类型层面排除掉。数据库侧有同名原生 ENUM
 * （prisma/schema.prisma 的 LlmModelRole）在存储层再挡一次拼写错误。
 */
export const LlmModelRoleSchema = z.enum(['chat', 'router', 'both']);
export type LlmModelRole = z.infer<typeof LlmModelRoleSchema>;

/**
 * 模型路由字段集中定义一次。理由与 `llmChannelFields` 完全相同：
 * PATCH 上带 `.default()` 会把"未传"落回默认值，于是"改个备注"会顺手把
 * `enabled` 改回 true、把 `priority` 改回 100 —— 停用中的模型被悄悄启用。
 *
 * `channelId` 允许在 PATCH 里改：把模型从一条渠道挪到另一条是合法运维动作
 * （换了服务商），强制"删了重建"会让它的优先级与审计信息一起丢掉。
 */
const llmModelRouteFields = {
  channelId: IdSchema,
  /** 上游模型名（原样送给 `POST /chat/completions`；大小写敏感，不做归一） */
  model: z.string().trim().min(1).max(128),
  role: LlmModelRoleSchema,
  enabled: z.boolean(),
  /** 全局优先级：越小越优先；同值按渠道 priority、再按创建时间决定 */
  priority: z.number().int().min(0).max(1000),
  remark: z.string().trim().max(200).nullable(),
} as const;

export const UpsertLlmModelRouteSchema = z
  .object({
    ...llmModelRouteFields,
    role: llmModelRouteFields.role.default('chat'),
    enabled: llmModelRouteFields.enabled.default(true),
    priority: llmModelRouteFields.priority.default(100),
    remark: llmModelRouteFields.remark.default(null),
  })
  .strict();
export type UpsertLlmModelRouteInput = z.infer<typeof UpsertLlmModelRouteSchema>;

export const PatchLlmModelRouteSchema = z
  .object(llmModelRouteFields)
  .partial()
  .extend({ id: IdSchema })
  .strict();
export type PatchLlmModelRouteInput = z.infer<typeof PatchLlmModelRouteSchema>;

/**
 * 上移/下移一行模型路由（v1.6.0，"管理模型使用优先级"的操作面）。
 *
 * 为什么不复用 `PATCH { priority }`：新建行的默认优先级都是 100，页面上"上移"
 * 两行优先级相同时改数字是**空操作**，而 ROOT 看到的是"点了没反应"。
 * 服务端因此按全局顺序整表重排（见 AdminModelRoutesService.move），
 * 一次请求内原子地完成"换位 + 编号"，并直接回新的列表（含生效模型），
 * 前端不必自己算新数字、也不必再发一次 GET 去确认。
 *
 * 边界行的 `up` / `down` 是**幂等**的（返回原列表），不报 400：
 * 按钮在首行时置灰只是 UI 细节，接口不该因为"没什么可做"而失败。
 */
export const MoveLlmModelRouteSchema = z
  .object({ direction: z.enum(['up', 'down']) })
  .strict();
export type MoveLlmModelRouteInput = z.infer<typeof MoveLlmModelRouteSchema>;

/* ---------------------- 邮箱发送渠道（v1.5.0，仅 ROOT） ---------------------- */

/**
 * 与 LLM 渠道同一套取舍：新建要求全给（带默认值），编辑全可选，两条 schema 分开写。
 * 不写 `UpsertEmailChannelSchema.partial()` 的理由与 LLM 渠道完全相同——
 * `partial()` 会把未传字段落回 `.default()`，把停用中的渠道悄悄打开。
 *
 * `apiKey` 与 `fromAddress` 是"发得出去"的两个必需要素：密钥决定有没有权限，
 * 发件地址决定服务商是否受理（大多数服务商要求发件域已验证）。
 */
const emailChannelFields = {
  name: z.string().trim().min(1).max(64),
  provider: EmailProviderSchema,
  baseUrl: z.string().trim().url().max(512),
  apiKey: z.string().trim().min(8).max(512),
  fromAddress: EmailSchema,
  fromName: z.string().trim().min(1).max(64),
  enabled: z.boolean(),
  priority: z.number().int().min(0).max(1000),
  timeoutMs: z.number().int().min(1000).max(60_000),
  remark: z.string().trim().max(200).nullable(),
} as const;

export const UpsertEmailChannelSchema = z
  .object({
    ...emailChannelFields,
    provider: emailChannelFields.provider.default('http_json'),
    fromName: emailChannelFields.fromName.default('智家教育'),
    enabled: emailChannelFields.enabled.default(true),
    priority: emailChannelFields.priority.default(100),
    timeoutMs: emailChannelFields.timeoutMs.default(15_000),
    remark: emailChannelFields.remark.default(null),
  })
  .strict();
export type UpsertEmailChannelInput = z.infer<typeof UpsertEmailChannelSchema>;

/** 编辑时省略 `apiKey` 表示"不改密钥"；给了才覆盖。 */
export const PatchEmailChannelSchema = z
  .object(emailChannelFields)
  .partial()
  .extend({ id: IdSchema })
  .strict();
export type PatchEmailChannelInput = z.infer<typeof PatchEmailChannelSchema>;

/** 发一封测试邮件：必须给收件人，否则"测试"这件事没有可观测的结果。 */
export const TestEmailChannelSchema = z
  .object({
    to: EmailSchema,
  })
  .strict();
export type TestEmailChannelInput = z.infer<typeof TestEmailChannelSchema>;

export const MAX_PAGE_LIMIT_ADMIN = 100;

export const AdminListUsersQuerySchema = z
  .object({
    keyword: z.string().trim().min(1).max(64).optional(),
    status: UserStatusSchema.optional(),
    role: UserRoleSchema.optional(),
    cursor: z.string().trim().min(1).max(256).optional(),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT_ADMIN).default(20),
  })
  .strict();
export type AdminListUsersQuery = z.infer<typeof AdminListUsersQuerySchema>;

export const AdminListConversationsQuerySchema = z
  .object({
    userId: UserIdSchema.optional(),
    keyword: z.string().trim().min(1).max(64).optional(),
    cursor: z.string().trim().min(1).max(256).optional(),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT_ADMIN).default(20),
  })
  .strict();
export type AdminListConversationsQuery = z.infer<typeof AdminListConversationsQuerySchema>;

export const AdminListMessagesQuerySchema = z
  .object({
    cursor: z.string().trim().min(1).max(256).optional(),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT_ADMIN).default(50),
  })
  .strict();
export type AdminListMessagesQuery = z.infer<typeof AdminListMessagesQuerySchema>;

export const ADMIN_USAGE_GROUP_BYS = ['user', 'channel', 'model', 'day'] as const;

export const AdminUsageQuerySchema = z
  .object({
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
    groupBy: z.enum(ADMIN_USAGE_GROUP_BYS).default('user'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type AdminUsageQueryInput = z.infer<typeof AdminUsageQuerySchema>;

/* ------------------------------ 响应 ------------------------------ */

/** 生效状态：已到期的临时封号在这里显示为 `active`（惰性判定，服务端不回写）。 */
export const AdminUserSummarySchema = z.object({
  id: UserIdSchema,
  role: UserRoleSchema,
  nickname: z.string().min(1).max(32),
  phone: PhoneSchema.nullable(),
  /**
   * v1.5.0 起必填（可为 null）。后台必须能按邮箱人工核对"这个人是谁"——
   * 邮箱验证码登录的用户**没有手机号**，只下发 phone 会让后台列表里出现
   * 一批"三个标识列全空"的行，客服无法定位。
   */
  email: EmailSchema.nullable(),
  status: UserStatusSchema,
  effectiveStatus: UserStatusSchema,
  suspendedAt: IsoDateTimeSchema.nullable(),
  suspendedUntil: IsoDateTimeSchema.nullable(),
  suspendReason: z.string().max(200).nullable(),
  lastLoginAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  conversationCount: z.number().int().min(0),
  messageCount: z.number().int().min(0),
});
export type AdminUserSummary = z.infer<typeof AdminUserSummarySchema>;

export const AdminUserDetailSchema = AdminUserSummarySchema.extend({
  avatarUrl: z.string().url().max(2048).nullable(),
  profile: ProfileSchema.nullable(),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

export const AdminUserPageSchema = cursorPageSchema(AdminUserSummarySchema);
export type AdminUserPage = z.infer<typeof AdminUserPageSchema>;

export const AdminConversationSummarySchema = z.object({
  id: IdSchema,
  userId: UserIdSchema,
  userNickname: z.string().min(1).max(32),
  title: z.string().max(100),
  domain: DomainSchema,
  ageBand: AgeBandSchema.nullable(),
  messageCount: z.number().int().min(0),
  lastMessageAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
});
export type AdminConversationSummary = z.infer<typeof AdminConversationSummarySchema>;

export const AdminConversationPageSchema = cursorPageSchema(AdminConversationSummarySchema);
export type AdminConversationPage = z.infer<typeof AdminConversationPageSchema>;

/** 后台消息：与用户端 MessageSchema 的差别是**带真实 token 与渠道 id**（见本段头注释）。 */
export const AdminMessageSchema = z.object({
  id: IdSchema,
  conversationId: IdSchema,
  role: MessageRoleSchema,
  content: z.string(),
  domain: DomainSchema.nullable(),
  ageBand: AgeBandSchema.nullable(),
  safetyFlag: SafetyFlagSchema,
  latencyMs: z.number().int().min(0),
  upstreamModel: z.string().max(128).nullable(),
  channelId: z.string().max(64).nullable(),
  promptTokens: z.number().int().min(0),
  completionTokens: z.number().int().min(0),
  createdAt: IsoDateTimeSchema,
});
export type AdminMessage = z.infer<typeof AdminMessageSchema>;

export const AdminMessagePageSchema = cursorPageSchema(AdminMessageSchema);
export type AdminMessagePage = z.infer<typeof AdminMessagePageSchema>;

export const AdminOrderSummarySchema = z.object({
  orderNo: OrderNoSchema,
  planId: PlanIdSchema,
  billingCycle: BillingCycleSchema,
  kind: OrderKindSchema,
  amountFen: z.number().int().min(0),
  status: OrderStatusSchema,
  channel: PaymentChannelSchema,
  paidAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminOrderSummary = z.infer<typeof AdminOrderSummarySchema>;

export const AdminQuotaGrantSchema = z.object({
  id: IdSchema,
  totalTokens: z.number().int().min(0),
  reason: z.string().max(200).nullable(),
  grantedByUserId: UserIdSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminQuotaGrant = z.infer<typeof AdminQuotaGrantSchema>;

export const AdminQuotaDetailSchema = z.object({
  planId: PlanIdSchema,
  unlimited: z.boolean(),
  dailyUsedTokens: z.number().int().min(0),
  dailyLimitTokens: z.number().int().min(0).nullable(),
  dailyResetAt: IsoDateTimeSchema,
  monthlyUsedTokens: z.number().int().min(0),
  monthlyGrantedTokens: z.number().int().min(0),
  monthlyLimitTokens: z.number().int().min(0).nullable(),
  monthlyResetAt: IsoDateTimeSchema,
  remainingPercent: z.number().int().min(0).max(100),
  grants: z.array(AdminQuotaGrantSchema),
});
export type AdminQuotaDetail = z.infer<typeof AdminQuotaDetailSchema>;

export const AdminPlanDetailSchema = z.object({
  planId: PlanIdSchema,
  billingCycle: BillingCycleSchema.nullable(),
  startsAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  orderNo: z.string().max(64).nullable(),
  orders: z.array(AdminOrderSummarySchema),
});
export type AdminPlanDetail = z.infer<typeof AdminPlanDetailSchema>;

export const LlmChannelSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(64),
  provider: LlmProviderSchema,
  baseUrl: z.string().max(512),
  enabled: z.boolean(),
  priority: z.number().int(),
  timeoutMs: z.number().int(),
  /**
   * 密钥**末尾 4 位**的展示串。服务端永远不下发明文或密文——
   * 后台页面需要的只是"确认填对了没有"，不是能拿走去用的凭据。
   */
  apiKeyHint: z.string().max(16),
  remark: z.string().max(200).nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type LlmChannel = z.infer<typeof LlmChannelSchema>;

export const LlmChannelListSchema = z.object({
  items: z.array(LlmChannelSchema),
  /**
   * 当前真正生效的渠道 id：按候选顺序取第一条**可用于对话**的模型路由所属渠道。
   * v1.6.0 之前是"唯一启用渠道"；模型路由表上线后"生效渠道"由模型候选顺序决定，
   * 因此这里必须用同一个解析器算出来，不能由服务端另行推导（否则页面显示的
   * '当前生效' 与实际打到的上游会在多候选场景下不一致）。
   */
  activeChannelId: IdSchema.nullable(),
  /** 上游来源：渠道表 / mock（AI_MOCK=true）/ 完全没有可用配置 */
  source: z.enum(['channel', 'mock', 'none']),
});
export type LlmChannelList = z.infer<typeof LlmChannelListSchema>;

export const LlmChannelTestResultSchema = z.object({
  ok: z.boolean(),
  httpStatus: z.number().int().min(0),
  latencyMs: z.number().int().min(0),
  message: z.string().max(500),
  modelCount: z.number().int().min(0).nullable(),
});
export type LlmChannelTestResult = z.infer<typeof LlmChannelTestResultSchema>;

/* ---------------------- 模型路由读取视图（v1.6.0，仅 ROOT） ---------------------- */

/**
 * 一行模型路由。`channelName` 是**冗余字段**（渠道表里的名字），
 * 刻意在这里展开而不是让前端拿着 channelId 去 items 里 join：
 * 模型优先级页要按全局顺序展示，顺序里必须能直接看到"这条模型挂在谁身上"，
 * 前端再拼一次既多一次查找，也会在渠道被删/改名时出现页面与列表不一致。
 */
export const LlmModelRouteSchema = z.object({
  id: IdSchema,
  channelId: IdSchema,
  channelName: z.string().min(1).max(64),
  /** 渠道是否启用。停用渠道上的模型路由不会进入候选，页面要能一眼看出原因 */
  channelEnabled: z.boolean(),
  model: z.string().min(1).max(128),
  role: LlmModelRoleSchema,
  enabled: z.boolean(),
  priority: z.number().int(),
  remark: z.string().max(200).nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type LlmModelRoute = z.infer<typeof LlmModelRouteSchema>;

export const LlmModelRouteListSchema = z.object({
  items: z.array(LlmModelRouteSchema),
  /** 按候选顺序（priority → 渠道 priority → 创建时间 → id）排序后的第一条对话模型 */
  activeChatModel: z.string().max(128).nullable(),
  activeRouterModel: z.string().max(128).nullable(),
  /** 生效对话模型所属渠道；没有任何可用候选时为 null */
  activeChannelId: IdSchema.nullable(),
  source: z.enum(['channel', 'mock', 'none']),
});
export type LlmModelRouteList = z.infer<typeof LlmModelRouteListSchema>;

/** 邮箱渠道（ROOT 专属视图）。密钥永远只下发末 4 位提示，与 LLM 渠道同一口径。 */
export const EmailChannelSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(64),
  provider: EmailProviderSchema,
  baseUrl: z.string().max(512),
  fromAddress: EmailSchema,
  fromName: z.string().min(1).max(64),
  enabled: z.boolean(),
  priority: z.number().int(),
  timeoutMs: z.number().int(),
  /** 密钥末尾 4 位展示串（形如 `••••a1b2`）。永远不下发明文或密文。 */
  apiKeyHint: z.string().max(32),
  remark: z.string().max(200).nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type EmailChannel = z.infer<typeof EmailChannelSchema>;

export const EmailChannelListSchema = z.object({
  items: z.array(EmailChannelSchema),
  /** 当前真正生效的渠道 id（mock 或无配置时为 null） */
  activeChannelId: IdSchema.nullable(),
  /** 上游来源：渠道表 / mock（非生产默认发信通道）/ 完全没配 */
  source: z.enum(['channel', 'mock', 'none']),
});
export type EmailChannelList = z.infer<typeof EmailChannelListSchema>;

/**
 * 发测试邮件的结果。刻意**不**回显服务商响应体：
 * 它可能包含收件人地址、message id 甚至账号片段，而后台页面只需要
 * "发出去了没有 + 上游怎么说的摘要"，摘要由服务端截断到 500 字符。
 */
export const EmailChannelTestResultSchema = z.object({
  ok: z.boolean(),
  httpStatus: z.number().int().min(0),
  latencyMs: z.number().int().min(0),
  message: z.string().max(500),
});
export type EmailChannelTestResult = z.infer<typeof EmailChannelTestResultSchema>;

/**
 * 邮箱发送能力状态（后台概览用）。与 `AdminAiStatusSchema` 同构。
 *
 * v1.6.0 起两者都是 `channel | mock | none` 三值：AI 侧删掉 env 回退档后也必须能表达
 * "一条可用候选都没有"，于是"邮箱比 AI 多一档 none"这个旧差异消失了。
 */
export const AdminEmailStatusSchema = z.object({
  /** 是否具备"真能发出去"的条件（渠道表有启用行，或非生产环境的 mock 模式） */
  configured: z.boolean(),
  mock: z.boolean(),
  fromAddress: EmailSchema.nullable(),
  source: z.enum(['channel', 'mock', 'none']),
  channelName: z.string().max(64).nullable(),
  reason: z.string().max(500).nullable(),
});
export type AdminEmailStatus = z.infer<typeof AdminEmailStatusSchema>;

export const AdminUsageGroupSchema = z.object({
  key: z.string().max(128),
  label: z.string().max(128),
  promptTokens: z.number().int().min(0),
  completionTokens: z.number().int().min(0),
  totalTokens: z.number().int().min(0),
  callCount: z.number().int().min(0),
});
export type AdminUsageGroup = z.infer<typeof AdminUsageGroupSchema>;

export const AdminUsageReportSchema = z.object({
  groupBy: z.enum(ADMIN_USAGE_GROUP_BYS),
  from: IsoDateTimeSchema,
  to: IsoDateTimeSchema,
  groups: z.array(AdminUsageGroupSchema),
  /** 合计是**整窗真值**，不随 limit 截断变化——否则页面上两个数字会互相矛盾。 */
  total: AdminUsageGroupSchema.omit({ key: true, label: true }),
});
export type AdminUsageReport = z.infer<typeof AdminUsageReportSchema>;

export const AdminAiStatusSchema = z.object({
  available: z.boolean(),
  mock: z.boolean(),
  model: z.string().max(128).nullable(),
  reason: z.string().max(500).nullable(),
  channelName: z.string().max(64).nullable(),
  /** 上游来源：渠道表 / mock（AI_MOCK=true）/ 无可用配置（v1.6.0 起不再有 env 档） */
  source: z.enum(['channel', 'mock', 'none']),
});
export type AdminAiStatus = z.infer<typeof AdminAiStatusSchema>;

export const AdminOverviewSchema = z.object({
  userCount: z.number().int().min(0),
  activeUserCount: z.number().int().min(0),
  suspendedUserCount: z.number().int().min(0),
  adminCount: z.number().int().min(0),
  conversationCount: z.number().int().min(0),
  messageCount: z.number().int().min(0),
  todayTokens: z.number().int().min(0),
  monthTokens: z.number().int().min(0),
  channelCount: z.number().int().min(0),
  enabledChannelCount: z.number().int().min(0),
  ai: AdminAiStatusSchema,
  /** v1.5.0：邮箱发信能力。后台概览必须能一眼看出"邮箱验证码登录现在能不能用"。 */
  email: AdminEmailStatusSchema,
});
export type AdminOverview = z.infer<typeof AdminOverviewSchema>;

/** 管理员账户（ROOT 专属视图）。刻意**不含** passwordHash、username 之外的任何凭据字段。 */
export const AdminAccountSchema = z.object({
  id: UserIdSchema,
  username: z.string().min(3).max(32),
  nickname: z.string().min(1).max(32),
  role: UserRoleSchema,
  status: UserStatusSchema,
  lastLoginAt: IsoDateTimeSchema.nullable(),
  passwordUpdatedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminAccount = z.infer<typeof AdminAccountSchema>;

export const AdminAccountListSchema = z.object({
  items: z.array(AdminAccountSchema),
  /** 全站唯一的 ROOT 账号（必然恰好一条，或为 null 表示还没建） */
  rootAccount: AdminAccountSchema.nullable(),
});
export type AdminAccountList = z.infer<typeof AdminAccountListSchema>;

/** 账号密码登录的响应与手机号登录同形：前端两条路径共用 `setSession`。 */
export const PasswordLoginResponseSchema = AuthResultSchema;
